import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaEye, FaClock, FaUserCheck, FaUserTimes, FaBook, FaTrash, FaEnvelope, FaUniversity, FaGraduationCap, FaCalendarAlt } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const AdminPkmBank = () => {
  usePageTitle('Admin PKM Bank');
  const { t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('pkm_bank_requests')
      .select('*')
      .order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      const { error } = await supabase
        .from('pkm_bank_requests')
        .update({ status: 'approved' })
        .eq('id', id);
      if (error) throw error;
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
    } catch (err) {
      alert('Gagal approve: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm(t('pkmBankAdmin.confirmReject'))) return;
    setProcessing(id);
    try {
      const { error } = await supabase
        .from('pkm_bank_requests')
        .update({ status: 'rejected' })
        .eq('id', id);
      if (error) throw error;
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    } catch (err) {
      alert('Gagal reject: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('pkmBankAdmin.confirmDelete'))) return;
    const { error } = await supabase.from('pkm_bank_requests').delete().eq('id', id);
    if (!error) setRequests(prev => prev.filter(r => r.id !== id));
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);

  const statusBadge = (status) => {
    if (status === 'pending') return <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaClock className="text-[10px]" /> {t('pkmBankAdmin.statusPending')}</span>;
    if (status === 'approved') return <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaUserCheck className="text-[10px]" /> {t('pkmBankAdmin.statusApproved')}</span>;
    return <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaUserTimes className="text-[10px]" /> {t('pkmBankAdmin.statusRejected')}</span>;
  };

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="font-inter bg-gray-50 dark:bg-dark-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-700 to-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FaBook className="text-amber-200" /> {t('pkmBankAdmin.title')}
          </h1>
          <p className="text-amber-100">{t('pkmBankAdmin.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { key: 'all', label: t('pkmBankAdmin.filterAll'), count: counts.all, color: 'bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300' },
            { key: 'pending', label: t('pkmBankAdmin.filterPending'), count: counts.pending, color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
            { key: 'approved', label: t('pkmBankAdmin.filterApproved'), count: counts.approved, color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
            { key: 'rejected', label: t('pkmBankAdmin.filterRejected'), count: counts.rejected, color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`p-4 rounded-xl text-left transition-all ${filter === f.key ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'} ${f.color}`}
            >
              <p className="text-2xl font-bold">{f.count}</p>
              <p className="text-sm font-medium">{f.label}</p>
            </button>
          ))}
        </div>

        {/* Reminder */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>💡 {t('pkmBankAdmin.reminderTitle')}</strong> {t('pkmBankAdmin.reminderDesc')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FaBook className="text-5xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">{t('pkmBankAdmin.noRequests')}</h3>
            <p className="text-gray-400 dark:text-gray-500">{t('pkmBankAdmin.noRequestsDesc')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(req => (
              <div key={req.id} className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden hover:shadow-md transition">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Avatar */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold shrink-0">
                        {req.name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{req.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
                          <FaEnvelope className="text-[10px]" /> {req.email}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                            <FaUniversity className="text-[10px]" /> {req.kampus}
                          </span>
                          <span className="bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                            <FaGraduationCap className="text-[10px]" /> {req.jurusan}
                          </span>
                          <span className="bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                            <FaCalendarAlt className="text-[10px]" /> {t('pkmBankAdmin.batchLabel')} {req.angkatan}
                          </span>
                          {statusBadge(req.status)}
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="text-sm text-gray-400 dark:text-gray-500 shrink-0">
                      {new Date(req.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setDetailModal(req)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 px-3 py-2 rounded-lg transition"
                      >
                        <FaEye /> Detail
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(req.id)}
                            disabled={processing === req.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 px-3 py-2 rounded-lg transition disabled:opacity-50"
                          >
                            <FaCheck /> {t('pkmBankAdmin.approve')}
                          </button>
                          <button
                            onClick={() => handleReject(req.id)}
                            disabled={processing === req.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 px-3 py-2 rounded-lg transition disabled:opacity-50"
                          >
                            <FaTimes /> {t('pkmBankAdmin.reject')}
                          </button>
                        </>
                      )}
                      {req.status !== 'pending' && (
                        <button
                          onClick={() => handleDelete(req.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 px-3 py-2 rounded-lg transition"
                        >
                          <FaTrash /> {t('pkmBankAdmin.delete')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Reason preview */}
                  {req.reason && (
                    <div className="mt-3 ml-[4.5rem] text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 rounded-lg px-3 py-2 italic">
                      "{req.reason}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetailModal(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="h-2 bg-gradient-to-r from-amber-500 to-orange-500"></div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-2xl font-bold">
                  {detailModal.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{detailModal.name}</h2>
                  <p className="text-gray-500 dark:text-gray-400">{detailModal.email}</p>
                  <div className="mt-1">{statusBadge(detailModal.status)}</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('pkmBankAdmin.detailInfo')}</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-400 dark:text-gray-500 text-xs">{t('pkmBank.campus')}</p>
                      <p className="font-medium text-gray-800 dark:text-gray-200">{detailModal.kampus}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 dark:text-gray-500 text-xs">{t('pkmBank.major')}</p>
                      <p className="font-medium text-gray-800 dark:text-gray-200">{detailModal.jurusan}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 dark:text-gray-500 text-xs">{t('pkmBank.batch')}</p>
                      <p className="font-medium text-gray-800 dark:text-gray-200">{detailModal.angkatan}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 dark:text-gray-500 text-xs">{t('pkmBankAdmin.submittedAt')}</p>
                      <p className="font-medium text-gray-800 dark:text-gray-200">
                        {new Date(detailModal.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('pkmBank.reason')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{detailModal.reason}</p>
                </div>

                {/* Copy email button */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">{t('pkmBankAdmin.emailToCopy')}</h3>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white dark:bg-dark-800 border border-blue-200 dark:border-blue-700/40 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 select-all">
                      {detailModal.email}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(detailModal.email);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium px-3 py-2 rounded-lg transition"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">{t('pkmBankAdmin.addToGDrive')}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                {detailModal.status === 'pending' && (
                  <>
                    <button
                      onClick={() => { handleApprove(detailModal.id); setDetailModal(prev => ({ ...prev, status: 'approved' })); }}
                      disabled={processing === detailModal.id}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <FaCheck /> {t('pkmBankAdmin.approve')}
                    </button>
                    <button
                      onClick={() => { handleReject(detailModal.id); setDetailModal(prev => ({ ...prev, status: 'rejected' })); }}
                      disabled={processing === detailModal.id}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <FaTimes /> {t('pkmBankAdmin.reject')}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setDetailModal(null)}
                  className="flex-1 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-semibold py-2.5 rounded-xl transition"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPkmBank;
